/**
 * Scenario library view (P12.3).
 *
 * Two-pane layout: left rail groups scenarios by SAMA BCM area
 * (cyber / third-party / physical), right pane renders the selected
 * scenario's metadata + counterfactual cascade. Cascade rendering
 * reuses CascadeImpactCard from P2.6 because the scenario cascade
 * endpoint returns the same CascadeSummary shape.
 *
 * Admin: + Instantiate from template opens the picker modal.
 *
 * Routes:
 *   /twin/scenarios                -- list view
 *   /twin/scenarios/:scenarioId    -- list + selected detail pane
 */
import { canRunOperations } from "shared/api/authority";
import { ThreadStrip } from "shared/thread/ThreadStrip";
import { FirstMeetings } from "shared/glossary/FirstMeetings";
import { ScenarioBasisSection } from "twin/scenarios/ScenarioBasisSection";
import { templateWords } from "twin/scenarios/templateWords";
import { ScenarioRegimeRefsSection } from "twin/scenarios/ScenarioRegimeRefsSection";
import {
  durationProvenanceSentence,
  isBankFigure,
} from "twin/scenarios/durationProvenance";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { CascadeImpactCard } from "twin/cascade/CascadeImpactCard";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { useMe } from "shared/api/useMe";
import type {
  CascadeSummary,
  ScenarioArea,
  ScenarioImpactRow,
  ScenarioSummary,
} from "shared/api/types";
import {
  useDeleteScenario,
  usePatchImpact,
  useScenarioCascade,
  useScenarioDetail,
  useScenarios,
} from "twin/api/scenario_hooks";

import { ScenarioMembersSection } from "./ScenarioMembersSection";
import { ScenarioTemplatePickerModal } from "./ScenarioTemplatePickerModal";
import { useToast } from "shared/toast/ToastProvider";

const AREA_LABEL: Record<ScenarioArea, string> = {
  cyber_technology: "Cyber / Technology",
  operational_third_party: "Operational / Third-Party",
  physical_environmental: "Physical / Environmental",
};

const AREA_TONE: Record<ScenarioArea, string> = {
  cyber_technology: "border-violet-300 bg-violet-50 hover:bg-violet-100",
  operational_third_party: "border-amber-300 bg-amber-50 hover:bg-amber-100",
  physical_environmental: "border-teal-300 bg-teal-50 hover:bg-teal-100",
};

const SEVERITY_TONE: Record<string, string> = {
  lost: "bg-rose-100 text-rose-900 border-rose-400",
  impaired: "bg-amber-100 text-amber-900 border-amber-400",
  partial: "bg-sky-100 text-sky-900 border-sky-400",
  none: "bg-slate-100 text-slate-700 border-slate-300",
};

export function ScenariosView() {
  const { t } = useTranslation();
  const { scenarioId } = useParams<{ scenarioId?: string }>();
  const me = useMe();
  // A8: the backend accepts head_of_opsres on schedule, create and run
  // (require_opsres_or_admin), and this hid the forms from her anyway.
  // The Resilience Lead could not schedule a drill in the product whose
  // operation is her job.
  const isAdmin = canRunOperations(me.data);
  const scenarios = useScenarios();
  const [pickerOpen, setPickerOpen] = useState(false);

  const grouped = useMemo(() => {
    const groups: Record<ScenarioArea, ScenarioSummary[]> = {
      cyber_technology: [],
      operational_third_party: [],
      physical_environmental: [],
    };
    for (const s of scenarios.data ?? []) {
      groups[s.area].push(s);
    }
    return groups;
  }, [scenarios.data]);

  return (
    <div
      data-testid="scenarios-view"
      className="flex flex-col md:flex-row gap-4 min-h-[400px]"
    >
      {/* Left rail */}
      <aside data-testid="scenarios-rail" className="md:w-72 space-y-3">
        <header className="flex items-baseline justify-between">
          <h1 className="text-lg font-semibold text-slate-900">
            {t("screens.scenariosView.scenarioLibrary")}
          </h1>
          <MarsadIcon
            name="scenario"
            size={22}
            className="me-2 inline-block align-[-0.12em] text-brand-brassDark"
          />
          {isAdmin && (
            <button
              type="button"
              data-testid="scenarios-instantiate-button"
              onClick={() => setPickerOpen(true)}
              className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-0.5 text-[11px] font-semibold text-brand-brassDark hover:bg-brand-brass/10"
            >
              {t("screens.scenariosView.instantiate")}
            </button>
          )}
        </header>

        {scenarios.isLoading && (
          <p className="text-xs text-slate-500">
            {t("screens.scenariosView.loadingScenarios")}
          </p>
        )}

        {scenarios.data && scenarios.data.length === 0 && (
          <p
            data-testid="scenarios-empty"
            className="rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600"
          >
            {t("screens.scenariosView.noScenariosYet")}{" "}
            {isAdmin && (
              <span>
                {t("screens.scenariosView.click")}{" "}
                <em>{t("screens.scenariosView.instantiate")}</em>{" "}
                {t("screens.scenariosView.toPickATemplate")}
              </span>
            )}
          </p>
        )}

        {(
          [
            "cyber_technology",
            "operational_third_party",
            "physical_environmental",
          ] as ScenarioArea[]
        ).map((area) => {
          const list = grouped[area];
          if (list.length === 0) return null;
          return (
            <section
              key={area}
              data-testid={`scenarios-group-${area}`}
              className="space-y-1"
            >
              <h2 className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {AREA_LABEL[area]}
              </h2>
              {list.map((s) => {
                const active = s.id === scenarioId;
                return (
                  <Link
                    key={s.id}
                    to={`/twin/scenarios/${encodeURIComponent(s.id)}`}
                    data-testid="scenario-rail-row"
                    data-scenario-id={s.id}
                    data-active={active}
                    className={
                      "block rounded border p-2 text-xs transition-colors " +
                      (active ? "border-emerald-500 bg-emerald-50" : AREA_TONE[area])
                    }
                  >
                    <div className="font-semibold text-slate-900 leading-tight">
                      {s.name}
                    </div>
                    <div className="mt-0.5 flex items-baseline gap-1 text-[10px] text-slate-600">
                      {/* Round 19 H55 -- the template's code (`tpl.sama.*`)
                          sat under every scenario; it says where it came
                          from in words, the code kept for the hover. */}
                      {s.template_code && (
                        <span
                          data-testid="scenario-rail-template"
                          title={s.template_code}
                        >
                          {templateWords(t, s.template_code)}
                        </span>
                      )}
                      {/* Round 12 H5 -- a flex gap draws the space and
                          puts nothing in the text, so this read
                          "cyber.ransomware_core- 2 impacts" to a copy,
                          a screen reader and an export. */}
                      <span>
                        {" \u00b7 "}
                        {s.impact_count} {t("screens.scenariosView.impacts")}
                      </span>
                    </div>
                    {/* ROUND 15 D12 -- a member is not hidden and not
                        pretended away: it is marked with where it sits. */}
                    {(s.member_of?.length ?? 0) > 0 && (
                      <div
                        data-testid="scenario-rail-member-of"
                        className="mt-0.5 text-[10px] text-brand-brassDark"
                      >
                        {t("screens.scenariosView.partOf", {
                          names: s.member_of!.map((m) => m.name).join(", "),
                        })}
                      </div>
                    )}
                  </Link>
                );
              })}
            </section>
          );
        })}
      </aside>

      {/* Right pane */}
      <main className="flex-1 space-y-3">
        {scenarioId ? (
          <SelectedScenarioPane scenarioId={scenarioId} isAdmin={isAdmin} />
        ) : (
          <p
            data-testid="scenarios-select-prompt"
            className="rounded border border-slate-200 bg-white p-6 text-center text-sm text-slate-600"
          >
            {t("screens.scenariosView.pickAScenarioFromTheLeftRail")}
          </p>
        )}
      </main>

      {pickerOpen && (
        <ScenarioTemplatePickerModal onClose={() => setPickerOpen(false)} />
      )}
    </div>
  );
}

interface SelectedScenarioPaneProps {
  scenarioId: string;
  isAdmin: boolean;
}

function SelectedScenarioPane({ scenarioId, isAdmin }: SelectedScenarioPaneProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const detail = useScenarioDetail(scenarioId);
  const scenarios = useScenarios();
  const cascade = useScenarioCascade(scenarioId);
  const deleteScenario = useDeleteScenario();
  const navigate = useNavigate();
  const qc = useQueryClient();

  if (detail.error) {
    return (
      <p
        data-testid="scenario-detail-error"
        className="rounded border border-rose-300 bg-rose-50 p-3 text-xs text-rose-800"
      >
        {t("screens.scenariosView.couldNotLoadScenario")}{" "}
        {detail.error instanceof Error
          ? detail.error.message
          : t("screens.scenariosView.unknown")}
      </p>
    );
  }

  if (!detail.data) {
    return (
      <p className="text-xs text-slate-500">{t("screens.scenariosView.loading")}</p>
    );
  }

  return (
    <div data-testid="scenario-detail-pane" className="space-y-3">
      <header className="rounded border border-slate-200 bg-white p-3">
        <div className="flex items-baseline justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{detail.data.name}</h2>
            <p className="font-mono text-[10px] text-slate-500">{detail.data.code}</p>
          </div>
          {isAdmin && (
            <button
              type="button"
              data-testid="scenario-delete-button"
              onClick={() => {
                if (confirm(`Delete scenario "${detail.data?.name}"?`)) {
                  // E2 -- deleting finished in silence, and the one
                  // action that removes something is the one whose
                  // confirmation matters most.
                  const name = detail.data?.name ?? "";
                  deleteScenario.mutate(
                    { scenarioId },
                    {
                      onSuccess: () => {
                        toast.push({
                          tone: "success",
                          message: t("screens.scenariosView.deletedToast", { name }),
                        });
                        /* Round 16 D22 -- the page went on asking for
                           the scenario it had just deleted: two 404s.
                           Leave it, and drop what was cached for it. */
                        qc.removeQueries({
                          predicate: (q) =>
                            JSON.stringify(q.queryKey).includes(scenarioId),
                        });
                        navigate("/twin/scenarios");
                      },
                      onError: (e) =>
                        toast.push({
                          tone: "error",
                          message: t("screens.scenariosView.deleteFailed"),
                          detail: e.detail,
                        }),
                    },
                  );
                }
              }}
              className="rounded border border-rose-300 bg-rose-50 px-2 py-0.5 text-[10px] uppercase tracking-wider text-rose-900 hover:bg-rose-100"
            >
              {t("screens.scenariosView.delete")}
            </button>
          )}
        </div>
        {/* Round 16 D22 -- the scenario could not be run or tested from
            its own page, tenth round. */}
        <p className="mt-2 flex flex-wrap gap-3 text-xs">
          <Link
            data-testid="scenario-run-in-war-room"
            to={`/twin/war-room?scenario=${encodeURIComponent(scenarioId)}`}
            className="text-brand-brassDark hover:underline"
          >
            {t("screens.scenariosView.runInWarRoom")}
          </Link>
          {/* Round 18 Q3 -- recording a run is the resilience desk's (and
              an administrator's); the CRO was offered it and landed on a
              page with no form for her. */}
          {isAdmin && (
            <Link
              data-testid="scenario-record-test"
              to={`/twin/test-runs?scenario=${encodeURIComponent(scenarioId)}`}
              className="text-brand-brassDark hover:underline"
            >
              {t("screens.scenariosView.recordTest")}
            </Link>
          )}
        </p>
        {detail.data.description && (
          <p className="mt-2 text-xs text-slate-700">{detail.data.description}</p>
        )}
        {detail.data.narrative_long && (
          <details className="mt-2">
            <summary className="text-[10px] uppercase tracking-wider text-slate-500 cursor-pointer">
              {t("screens.scenariosView.narrative")}
            </summary>
            <p className="mt-1 text-xs text-slate-700">{detail.data.narrative_long}</p>
          </details>
        )}
        {detail.data.regulator_alignment && (
          <p className="mt-2 text-[11px] text-slate-600">
            <span className="font-semibold uppercase tracking-wider text-slate-500">
              {t("screens.scenariosView.regulatorAlignment")}
            </span>{" "}
            {detail.data.regulator_alignment}
          </p>
        )}
      </header>
      {/* Stitching review 5.2 -- the scenario's thread: whether the
          regime asks for it, what it strikes, the verdict it draws, the
          recorded basis, and the runs that tested it. */}
      <ThreadStrip subject="scenario" id={scenarioId} />
      {/* Stitching review 5.5 -- a scenario's duration is the
          regulator's when stated; taught once. */}
      <FirstMeetings terms={["stated_duration"]} />
      <ScenarioBasisSection
        scenarioId={scenarioId}
        detail={detail.data}
        canEdit={isAdmin}
      />
      <ScenarioRegimeRefsSection
        scenarioId={scenarioId}
        detail={detail.data}
        canEdit={isAdmin}
      />

      {/* P419.1 -- composition, which P418 built and could not author. */}
      <ScenarioMembersSection
        scenarioId={scenarioId}
        scenarios={Array.isArray(scenarios.data) ? scenarios.data : []}
        isAdmin={isAdmin}
      />

      {/* Impact list */}
      <section
        data-testid="scenario-impacts"
        className="rounded border border-slate-200 bg-white p-3"
      >
        <h3 className="text-xs font-semibold text-slate-900 mb-2">
          {t("screens.scenariosView.impacts2")}
          {detail.data.impacts.length})
        </h3>
        {detail.data.impacts.length === 0 ? (
          <p className="text-xs italic text-slate-500">
            {t("screens.scenariosView.noImpactsOnThisScenario")}
          </p>
        ) : (
          <ul className="space-y-1">
            {detail.data.impacts.map((i) => (
              <li
                key={i.id}
                data-testid="scenario-impact-row"
                className="flex items-baseline gap-2 text-xs"
              >
                <span
                  className={`inline-block rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                    SEVERITY_TONE[i.severity ?? "none"]
                  }`}
                >
                  {i.severity ?? t("screens.scenariosView.none")}
                </span>
                <span className="font-mono text-[10px] text-slate-500">
                  {i.target_kind}
                </span>
                <span className="text-slate-800">
                  {i.target_name ?? t("screens.scenariosView.deleted")}
                </span>
                {i.notes && <span className="text-slate-500">-- {i.notes}</span>}
                <ImpactDuration scenarioId={scenarioId} impact={i} isAdmin={isAdmin} />
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Counterfactual cascade */}
      <section data-testid="scenario-cascade-section" className="space-y-2">
        <h3 className="text-xs font-semibold text-slate-900">
          {t("screens.scenariosView.counterfactualCascade")}
        </h3>
        {cascade.isLoading && (
          <p className="text-xs text-slate-500">
            {t("screens.scenariosView.computingCascade")}
          </p>
        )}
        {cascade.data && cascade.data.impacted_count === 0 && (
          <p
            data-testid="scenario-cascade-empty"
            className="rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600"
          >
            {t("screens.scenariosView.noCascadeNodesTheScenarioSImpacts")}
          </p>
        )}
        {cascade.data && cascade.data.impacted_count > 0 && (
          <p data-testid="scenario-cascade-union" className="text-xs text-slate-600">
            {/* Round 20 P24 -- struck, then reached beyond. */}
            {struckLine(cascade.data, t)}
          </p>
        )}
        {cascade.data && cascade.data.impacted_count > 0 && (
          <div
            data-testid="scenario-cascade-grid"
            className="grid grid-cols-1 md:grid-cols-2 gap-2"
          >
            {cascade.data.impacted.map((impact) => (
              <CascadeImpactCard
                key={`${impact.entity_kind}-${impact.entity_id}`}
                impact={impact}
                counterfactual
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/** Round 20 P24 -- what the scenario strikes, by register status, and
 *  what the walk reaches beyond it. "3 admitted reached" sat beside
 *  five admitted cards: the reach counted only downstream of them. */
function struckLine(data: CascadeSummary, t: TFunction): string {
  const ibs = data.impacted.filter((i) => i.entity_kind === "ibs_service");
  const services = ibs.filter(
    (i) => i.service_class !== "internal" && i.admission_status === "admitted",
  ).length;
  const functions = ibs.filter((i) => i.service_class === "internal");
  const fnCandidates = functions.filter(
    (i) => i.admission_status !== "admitted",
  ).length;
  return t("screens.scenariosView.struckReached", {
    struck: t("screens.scenariosView.struckServices", { count: services }),
    fnStruck:
      functions.length > 0
        ? t("screens.scenariosView.struckFunctions", {
            count: functions.length,
            status:
              fnCandidates === functions.length
                ? t("screens.scenariosView.struckUnderAssessment")
                : "",
          })
        : "",
    reached: data.cascade_ibs_count,
    all: data.cascade_ibs_count + (data.cascade_candidate_union_count ?? 0),
    fn:
      (data.cascade_function_union_count ?? 0) > 0
        ? t("screens.scenariosView.reachUnionFunctions", {
            count: data.cascade_function_union_count ?? 0,
          })
        : "",
  });
}

/**
 * ROUND 15 D14 -- what the source states about one target's time,
 * said for every reader, and settable by an operator. A figure with no
 * basis is a stated duration; clearing the figure makes it unstated;
 * `until recovered` carries no figure. The server refuses the shapes
 * the database would (a stated basis with no figure, a range upside
 * down) and the refusal is printed here in its own words.
 */
function ImpactDuration({
  scenarioId,
  impact,
  isAdmin,
}: {
  scenarioId: string;
  impact: ScenarioImpactRow;
  isAdmin: boolean;
}) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const patch = usePatchImpact(scenarioId);
  const [editing, setEditing] = useState(false);
  const [basis, setBasis] = useState<"stated" | "until_recovered" | "unstated">(
    impact.duration_basis ?? "unstated",
  );
  const [hours, setHours] = useState(
    impact.duration_hours != null ? String(impact.duration_hours) : "",
  );
  const [max, setMax] = useState(
    impact.duration_hours_max != null ? String(impact.duration_hours_max) : "",
  );

  const bankFigure = isBankFigure({
    set_by: impact.duration_set_by,
    source_basis: impact.duration_source_basis,
    source_hours: impact.duration_source_hours,
    source_hours_max: impact.duration_source_hours_max,
    basis: impact.duration_basis,
    hours: impact.duration_hours,
    hours_max: impact.duration_hours_max,
  });

  const statement = (() => {
    const b = impact.duration_basis ?? "unstated";
    if (b === "stated" && impact.duration_hours != null) {
      return impact.duration_hours_max != null
        ? t(
            bankFigure
              ? "screens.scenariosView.duration.setRange"
              : "screens.scenariosView.duration.statedRange",
            {
              hours: impact.duration_hours,
              max: impact.duration_hours_max,
            },
          )
        : t(
            bankFigure
              ? "screens.scenariosView.duration.set"
              : "screens.scenariosView.duration.stated",
            { hours: impact.duration_hours },
          );
    }
    if (b === "until_recovered")
      return t("screens.scenariosView.duration.untilRecovered");
    return t("screens.scenariosView.duration.unstated");
  })();

  const provenance = durationProvenanceSentence(t, i18n.language, {
    set_by: impact.duration_set_by,
    set_at: impact.duration_set_at,
    source_label: impact.duration_source_label,
    source_basis: impact.duration_source_basis,
    source_hours: impact.duration_source_hours,
    source_hours_max: impact.duration_source_hours_max,
    basis: impact.duration_basis,
    hours: impact.duration_hours,
    hours_max: impact.duration_hours_max,
  });

  const save = () => {
    const body =
      basis === "stated"
        ? {
            duration_basis: "stated" as const,
            duration_hours: hours.trim() === "" ? null : Number(hours),
            duration_hours_max: max.trim() === "" ? null : Number(max),
          }
        : { duration_basis: basis, duration_hours: null, duration_hours_max: null };
    patch.mutate(
      { impactId: impact.id, body },
      {
        onSuccess: () => {
          setEditing(false);
          toast.push({
            tone: "success",
            message: t("screens.scenariosView.duration.saved"),
          });
        },
        onError: (e) =>
          toast.push({
            tone: "error",
            message: t("screens.scenariosView.duration.failed"),
            detail: e.detail,
          }),
      },
    );
  };

  return (
    <span
      data-testid={`scenario-impact-duration-${impact.id}`}
      data-basis={impact.duration_basis ?? "unstated"}
      className="inline-flex flex-wrap items-center gap-1 text-[11px] text-slate-600"
    >
      <span data-testid={`scenario-impact-duration-text-${impact.id}`}>
        {" \u00b7 "}
        {/* Round 16 D18 -- whose the figure is. Untouched, the source's
            own words ("SAMA states 72h"); set by the bank, the figure
            and who set it, beside what the source states. */}
        {provenance && !impact.duration_set_by
          ? provenance
          : provenance
            ? `${statement} \u2014 ${provenance}`
            : statement}
      </span>
      {impact.capacity_loss_pct != null && (
        <span>
          {" \u00b7 "}
          {t("screens.scenariosView.duration.capacity", {
            pct: impact.capacity_loss_pct,
          })}
        </span>
      )}
      {isAdmin && !editing && (
        <button
          type="button"
          data-testid={`scenario-impact-duration-edit-${impact.id}`}
          onClick={() => setEditing(true)}
          className="text-brand-brassDark hover:underline"
        >
          {t("screens.scenariosView.duration.edit")}
        </button>
      )}
      {isAdmin && editing && (
        <span className="inline-flex flex-wrap items-center gap-1">
          <label className="inline-flex items-center gap-1">
            <span className="sr-only">
              {t("screens.scenariosView.duration.basisLabel")}
            </span>
            <select
              data-testid={`scenario-impact-duration-basis-${impact.id}`}
              value={basis}
              onChange={(e) =>
                setBasis(e.target.value as "stated" | "until_recovered" | "unstated")
              }
              className="rounded border border-slate-300 px-1 py-0.5 text-[11px]"
            >
              {(["stated", "until_recovered", "unstated"] as const).map((b) => (
                <option key={b} value={b}>
                  {/* Round 17 D23 -- a figure the bank set is not
                      "stated by the source". Round 18 D25 -- and a
                      figure set here is the bank's from the start: the
                      editor offered "Stated by the source" on its first
                      opening over SAMA's figure. */}
                  {t(
                    b === "stated"
                      ? "screens.scenariosView.duration.basis.statedByBank"
                      : `screens.scenariosView.duration.basis.${b}`,
                  )}
                </option>
              ))}
            </select>
          </label>
          {basis === "stated" && (
            <>
              <input
                type="number"
                min={0}
                step="0.5"
                aria-label={t("screens.scenariosView.duration.hours")}
                data-testid={`scenario-impact-duration-hours-${impact.id}`}
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                className="w-16 rounded border border-slate-300 px-1 py-0.5 text-[11px]"
              />
              <input
                type="number"
                min={0}
                step="0.5"
                aria-label={t("screens.scenariosView.duration.max")}
                data-testid={`scenario-impact-duration-max-${impact.id}`}
                value={max}
                onChange={(e) => setMax(e.target.value)}
                className="w-16 rounded border border-slate-300 px-1 py-0.5 text-[11px]"
              />
            </>
          )}
          <button
            type="button"
            data-testid={`scenario-impact-duration-save-${impact.id}`}
            onClick={save}
            disabled={patch.isPending}
            className="rounded border border-brand-brass/40 bg-brand-brass/5 px-1.5 py-0.5 text-brand-brassDark disabled:opacity-50"
          >
            {t("screens.scenariosView.duration.save")}
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="text-slate-500 hover:underline"
          >
            {t("screens.scenariosView.duration.cancel")}
          </button>
        </span>
      )}
    </span>
  );
}
